<script lang="ts">
import { getCollaboAndChannelUserList, getContentDetail } from '@/api/content';
import { CategoryConnectRecEntityDto } from '@/api/dto/category.dto';
import { SearchCollaboRoomUserListDto } from '@/api/dto/collabo.dto';
import { ContentEntityDto } from '@/api/dto/content.dto';
import ApocImageSet from '@/components/common/ApocImageSet.vue';
import AppConfig from '@/constants';
import { RESOLUTION_TYPE } from '@/stores/data-store';
import { POPUP_MODE } from '@/stores/state-store';
import { initStore } from '@/stores/store-manager';
import { DEFECT_TYPE_CODE, OPEN_TYPE_CODE, STATE_YN } from '@/types';
import { getApiClient } from '@/utils/apiClient';
import { goAuthor, goAuthorContextInterface } from '@/utils/apoc-utils';
import { ShareType, generateShortShareLink, getDiffTimeFormat, getImgUrl, loadLocalData } from '@/utils/common-util';
import moment from 'moment/moment';
import { PropType, defineComponent, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

interface ICallbacks {
  getList(): void;
}

export default defineComponent({
  name: 'MainContentsItem',
  components: { ApocImageSet },
  props: {
    data: {
      type: Object as PropType<CategoryConnectRecEntityDto | ContentEntityDto>,
      default: ContentEntityDto,
    },
    callbacks: {
      type: Object as PropType<ICallbacks>,
    },
    isVertical: {
      type: Boolean,
      default: false,
    },
  },

  setup: function (props, context) {
    const { t } = useI18n({ useScope: 'global' });
    const router = useRouter();
    const storeManager = initStore();
    const apiClient = getApiClient(AppConfig.API_SERVER, storeManager);
    const isOpenOption = ref<boolean>(false); // state of option popup
    const isNotTemplate = ref<boolean>(true);
    const lang = loadLocalData(AppConfig.KEYS.CONST.CHANGE_LANG);
    const loginUser = ref();
    const openTypeValue = ref<string>(''); //콘텐츠 공개상태 값
    const openTypeLabel = ref<string>(''); //콘텐츠 공개상태
    const isCollabOrChannel = ref<boolean>(false);
    const editAble = ref(false); // 콘텐츠 주인이거나, 채널, 콘텐츠에 협업되어 있으면 수정권한 있음
    const isCrackOpen = ref<boolean>(false);

    // 콘텐츠가 삭제되었는지 확인
    const contentDeleteCheck = async (contentIdx: string) => {
      const param = new ContentEntityDto();
      param.contentIdx = contentIdx as string;
      const res = await getContentDetail(apiClient, param);
      if (res.resultCode !== 0) {
        window.alert(t('msg.' + res.resultMsg));
        if (props.callbacks && props.callbacks.getList) props.callbacks.getList();
        return null;
      }
      return res;
    };

    const onClickItem = () => {
      // 아이템을 눌러 콘텐츠 상세 페이지에 들어갔다가 다시 돌아오는 경우, 이전에 열려있던 페이지를 기억
      if (props.data) {
        storeManager.dataStore.setScrollPosition(window.scrollY);
        router.push({ path: `/content/${props.data.contentIdx}` });
        //플레이한 기록을 바로 새로고침해주기 위해서 콘텐츠 리스트를 새로고침 해준다
        if (props.callbacks && props.callbacks.getList) props.callbacks.getList();
      }
    };

    /**
     * 옵션창 열기
     * @author dopamine
     * @data 220331
     */
    const onClickOption = () => {
      if (isOpenOption.value) isOpenOption.value = false;
      if (storeManager.dataStore.page.resolution >= RESOLUTION_TYPE.TABLET) {
        isOpenOption.value = true;
      } else {
        if (props.data) {
          if (props.data.hasOwnProperty('contentIdx') && props.data.hasOwnProperty('favContent') && props.data.hasOwnProperty('shortUrl')) {
            storeManager.dataStore.setPopupContentId(props.data.contentIdx);
            storeManager.dataStore.setIsFav(props.data.favContent);
            storeManager.dataStore.setPopupShortUrl(props.data.shortUrl as string);
            storeManager.dataStore.setPopupContentsOptionType('');
            storeManager.stateStore.setPopupMode(POPUP_MODE.CONTENTS_OPTION_MODAL);
            isOpenOption.value = true;
          }
        }
      }
    };

    /**
     * 옵션 팝업 닫기
     * @author dopamine
     * @date 220331
     */
    const onClickCloseOption = () => {
      isOpenOption.value = false; // 팝업창 닫아주는 역할
      if (storeManager.dataStore.page.resolution < RESOLUTION_TYPE.TABLET) {
        storeManager.dataStore.setPopupContentId('');
        storeManager.dataStore.setPopupContentsOptionType('');
        storeManager.stateStore.setPopupMode(POPUP_MODE.NONE);
      }
    };

    /**
     * 수정하기 (저작도구 연결)
     * @author dopamine
     * @date 220404
     */
    const onClickEdit = async () => {
      const { contentIdx } = props.data;
      const deleteCheck = await contentDeleteCheck(contentIdx);
      if (!deleteCheck) return;
      isOpenOption.value = false;
      storeManager.dataStore.setModifyContentIdx(contentIdx);
      storeManager.dataStore.setModifyContentType(null);
      storeManager.stateStore.setOpenAuthor(true);

      const param: goAuthorContextInterface = {
        authorUrl: `${AppConfig.AUTHOR_HOST}author?dx=${contentIdx}&lang=${loadLocalData(AppConfig.KEYS.CONST.CHANGE_LANG)}`,
        ctx: JSON.stringify({ id: contentIdx, lang: loadLocalData(AppConfig.KEYS.CONST.CHANGE_LANG) }),
        target: `content_${contentIdx}` + (!isNotTemplate.value ? `&current_time=${moment()}` : ''),
      };
      goAuthor(param, storeManager);
    };

    /**
     * 공유하기 기능
     * @param type
     * @author Tibia
     * @date 220427
     * @modify pubis 221005
     * @modify bigtoe 230519
     */
    const onClickShare = async (type: ShareType) => {
      // const deleteCheck = await contentDeleteCheck(props.data.contentIdx);
      // if (!deleteCheck) return;
      // if (props.data.shortUrl) generateShortShareLink(props.data.shortUrl);
      //
      // isOpenOption.value = false;
      // if (!storeManager.stateStore.popupCopyState) {
      // 	storeManager.stateStore.setPopupCopyState(true);
      // 	setTimeout(() => {
      // 		storeManager.stateStore.setPopupCopyState(false);
      // 	}, 3000);
      // }

      const deleteCheck = await contentDeleteCheck(props.data?.contentIdx as string);
      if (!deleteCheck) return;
      storeManager.dataStore.setPopupContentId(props.data?.contentIdx as string);
      storeManager.stateStore.setPopupMode(POPUP_MODE.CONTENT_SHARE_POPUP);
      onClickCloseOption();
    };

    const getPlayCount = (v: number): string => {
      if (lang === 'ko') {
        if (v < 1000) {
          return v.toLocaleString() + t('common.times');
        } else if (10000 > v && v >= 1000) {
          //n.n천 회
          return parseFloat((v / 1000).toFixed(1)).toLocaleString() + t('common.thousand') + ' ' + t('common.times');
        } else {
          //n.n만 회
          return parseFloat((v / 10000).toFixed(1)).toLocaleString() + t('common.tenThousand') + ' ' + t('common.times');
        }
      } else {
        if (v < 1000) {
          return v.toLocaleString();
        } else if (1000000 > v && v >= 1000) {
          //n.nK
          return parseFloat((v / 1000).toFixed(1)).toLocaleString() + t('common.thousand');
        } else {
          //n.nM
          return parseFloat((v / 1000000).toFixed(1)).toLocaleString() + 'M';
        }
      }
    };

    // 신고하기
    const onClickReport = () => {
      storeManager.dataStore.setPopupReportTargetId(props.data.contentIdx);
      storeManager.dataStore.setPopupReportTargetType(DEFECT_TYPE_CODE.BOARD_COMMENT);
      storeManager.stateStore.setPopupMode(POPUP_MODE.REPORT_POPUP);
    };

    // 출처 조회
    const onClickCopyright = () => {
      const scrollWidth = document.documentElement.clientWidth + 'px';
      document.body.style.width = scrollWidth;
      document.getElementsByTagName('header')[0].style.width = scrollWidth;
      storeManager.dataStore.setCopyrightContentIdx(props.data.contentIdx);
      storeManager.stateStore.setPopupMode(POPUP_MODE.COPYRIGHT_POPUP);
    };

    /**
     * 접근 권한, 수정 권한 부여
     */
    const authorizeAccessContents = () => {
      if (isCollabOrChannel.value) {
        isCrackOpen.value = true;
        editAble.value = true;
      } else {
        //내가 쓴 글인지 확인
        //regrUserIdx가 null이고 로그인 안 한 상태에서 true로 변경되어 조건 수정함
        if (props.data.regrUserIdx && props.data?.regrUserIdx === loginUser.value?.userIdx) {
          editAble.value = true;
          isCrackOpen.value = true;
        } else {
          editAble.value = false;
          isCrackOpen.value = (props.data as ContentEntityDto).openTypeCode !== OPEN_TYPE_CODE.PRIVATE;
        }
      }
      // verifyAccessContents();
    };

    /**
     * 콘텐츠 접근권한 있는 유저 조회
     */
    const apiGetCollaboAndChannelUserList = async () => {
      const param = new SearchCollaboRoomUserListDto();
      param.contentIdx = props.data.contentIdx;
      await getCollaboAndChannelUserList(apiClient, param).then((res) => {
        if (res.resultCode === 0) {
          if (res.data && res.data.length > 0) {
            // 채널 및 콘텐츠 협업자 명단
            res.data.map((item) => {
              if (loginUser.value && item.userIdx === loginUser.value?.userIdx) {
                // isCrackOpen.value = true;
                editAble.value = true;
                isCollabOrChannel.value = true;
              }
            });
          }
        }
      });
    };

    /**
     * 로그인 유저인지 1차 확인
     */
    const verifyLogin = async () => {
      loginUser.value = JSON.parse(loadLocalData(AppConfig.KEYS.CONST.LOGIN_USER) as string);
      if (loginUser.value) {
        await apiGetCollaboAndChannelUserList();
      } else {
        editAble.value = false;
        //OPEN_TYPE_CODE가 Private이면 false, 그 외는 모두 true
        isCrackOpen.value = !((props.data as ContentEntityDto)?.openTypeCode === OPEN_TYPE_CODE.PRIVATE);
      }
      authorizeAccessContents();
    };

    onMounted(() => {
      // verifyLogin();
    });

		return {
			props,
			t,
			storeManager,
			isOpenOption,
			editAble,
			ShareType,
			RESOLUTION_TYPE,
			openTypeLabel,
			openTypeValue,
			OPEN_TYPE_CODE,
			getImgUrl,
			getDiffTimeFormat,
			getPlayCount,
			onClickItem,
			onClickOption,
			onClickCloseOption,
			onClickShare,
			onClickReport,
			onClickCopyright,
			onClickEdit,
			lang,
			STATE_YN,
		};
	},
});
</script>

<template>
	<div @click="onClickItem">
		<div class="thumbnail-area">
			<apoc-image-set :origin-src="data.contentMainImg" :src="getImgUrl(data.contentMainImg, '', isVertical && true)" class="img-contain" />
		</div>
		<div class="info-area">
			<!-- <div class="info-profile">
				<apoc-image-set :src="getImgUrl(data?.userProfileImg)" />
			</div> -->
			<div class="info-body">
				<div class="info-category">{{ (lang === 'ko' ? data.categoryName : data.categoryEnName) || t('common.category') }}</div>
				<div class="info-title-wrapper">
					<div class="info-title">{{ data.contentName }}</div>
					<!-- <div class="content-select">
						<div class="edit" @click="onClickOption">
							<apoc-image-set src="/assets/images/common/icons/more-option.svg" />
						</div>
						<transition name="dropdown">
							<div
								v-click-away="onClickCloseOption"
								v-if="isOpenOption && storeManager.dataStore.page.resolution >= RESOLUTION_TYPE.TABLET"
								class="option-list-area-wrapper">
								<ul class="option-list-area">
									<apoc-link href="#" @click="onClickShare(ShareType.COPY_LINK)">
										<apoc-image-set src="/assets/images/common/icons/mypage/icon_share.svg" />{{ t('common.shareContents') }}
									</apoc-link>
									<apoc-link href="#" @click="onClickReport">
										<apoc-image-set src="/assets/images/common/icons/mypage/icon_emoji_flags.svg" />{{ t('common.report') }}
									</apoc-link>
									<apoc-link href="#" @click="onClickCopyright">
										<apoc-image-set src="/assets/images/common/icons/mypage/icon_info.svg" />{{ t('common.source') }}
									</apoc-link>
									<apoc-link v-if="editAble" class="manage-content" href="#" @click="onClickEdit">
										<apoc-image-set src="/assets/images/common/icons/mypage/icon_draw-polygon2.svg" />{{ t('common.manageContent') }}
									</apoc-link>
								</ul>
							</div>
						</transition>
					</div> -->
				</div>
				<!-- <div class="info-tag-area">
					<apoc-button class="info-tag tag-limit-access" :class="openTypeValue === OPEN_TYPE_CODE.PRIVATE ? 'private' : ''">
						{{ openTypeLabel }}
					</apoc-button>
					<div class="info-tag tag-apoc">{{ t('common.apoc') }}</div>
					<div class="info-tag tag-unreal">{{ t('common.etc') }}</div>
				</div> -->
				<!-- APOC 내부 컨텐츠 -->
				<div class="common-tag" v-if="data.externalContentConnectYn === STATE_YN.N">apoc</div>
				<!-- 외부 컨텐츠 -->
				<div class="common-tag etc" v-else>etc</div>
				<p class="info-nick">{{ data.userNicknm }}</p>
				<!-- <div class="info-etc">
						<span>{{ t(`common.play`) }} </span>
						<span>{{ getPlayCount(Number(data.playCount)) }}</span>
						<span><i /></span>
						<span>{{ data.modDt ? getDiffTimeFormat(data.modDt).toString() : getDiffTimeFormat(data.regDt).toString() }}</span>
					</div> -->
			</div>
		</div>
	</div>
</template>

<style scoped></style>
